import {CommonApi} from '@/common/api/CommonApi';
import {UserNotificationApi} from '@/common/api/notification/UserNotificationApi';
import {UserSwitchApi} from '@/common/api/user/UserSwitchApi';
import {ref} from 'vue';
import {BaseApi} from '@/common/api/BaseApi';
import {ErrorDialog} from '@simpleforyou/client-core';
import {INotification} from '@/types/composables/layout/IBaseLayout';
import {LoginUser} from '@/types/LoginUser';
import {IFooterLink, IPartnerShop, ISocialMedia} from '@/types/layout/ILayoutInterfaces';
import {useApiClient} from '@/common/api/useApiClient';
import {useStore} from '@/common/store/store';
import {useAuth} from '@/composables/useAuth';
import {clearUserSwitchTarget} from '@/common/auth/userSwitchState';
import {reloadPage} from '@/common/utils/windowUtils';

export const partnerShops = ref<IPartnerShop[]>([]);
export const socialMedias = ref<ISocialMedia[]>([]);
export const footerLinks = ref<IFooterLink[]>([]);
export const shortCalendar = ref(false);
export const showNotification = ref(false);
const oldNotificationCount = ref(0);
export const notifications = ref<INotification[]>([]);

export function logoUrl() {
  const store = useStore();
  // Returns '' during the brief window between session reset (logout, hard-cut)
  // and the layout actually unmounting — otherwise reading .customer on
  // undefined crashes VNavigationDrawer.
  const customer = store.userData?.customer;
  if (!customer) {
    return '';
  }
  return `${BaseApi.IMAGE_URL}/files/${customer}/layout/logo.jpg`;
}

export async function loadFooterLinks() {
  const apiClient = useApiClient();
  await apiClient.get<IFooterLink[]>(CommonApi.footerLinks()).then((response) => {
    if (response.status === 200) {
      footerLinks.value = response?.data;
    }
  }).catch(() => {
    new ErrorDialog();
  });
}

export async function loadSocialMedia() {
  const apiClient = useApiClient();
  await apiClient.get<ISocialMedia[]>(CommonApi.socialMedia()).then((response) => {
    if (response.status === 200) {
      socialMedias.value = response?.data;
    }
  }).catch(() => {
    new ErrorDialog();
  });
}

export async function loadPartnerShops() {
  const apiClient = useApiClient();
  await apiClient.get<IPartnerShop[]>(CommonApi.partnerShop()).then((response) => {
    if (response.status === 200) {
      partnerShops.value = response?.data;
    }
  }).catch(() => {
    new ErrorDialog();
  });
}

export async function loadNotifications() {
  const apiClient = useApiClient();
  await apiClient.get<INotification[]>(UserNotificationApi.api(), false).then((response) => {
    if (response.status === 200) {
      notifications.value = response?.data;
      const menuNotification = document.getElementById('menuNotifications');
      if (notifications.value.length > oldNotificationCount.value) {
        menuNotification?.classList.add('shake');
        oldNotificationCount.value = notifications.value.length;
      } else {
        menuNotification?.classList.remove('shake');
      }
    }
  }).catch(() => {
    new ErrorDialog();
  });
}

export async function removeNotification(notificationId: string) {
  const notification = notifications.value.find((noti) => noti.id === notificationId);
  if (notification) {
    notifications.value.removeItem(notification);
  }
  const apiClient = useApiClient();
  await apiClient.delete(UserNotificationApi.api({id: notificationId})).catch(() => {
    new ErrorDialog();
  });
}

export async function returnUser() {
  const store = useStore();
  const apiClient = useApiClient();
  // Backend returns {accessToken, refreshToken, user: UserData} — user data
  // is nested, not on the top level (unlike legacy PHP).
  await apiClient.post<LoginUser>(UserSwitchApi.api(), {}).then(async (response) => {
    if (response.status === 200 && response.data?.user) {
      // Back to admin → drop the durable switch intent, otherwise the next
      // reload's reexchangeUserSwitch() would re-derive the switch from the
      // still-stored target and silently switch back in (this header entry is a
      // second revert path next to UserSwitch.vue's revertSwitch — both MUST
      // clear the target).
      clearUserSwitchTarget();
      store.setAccessToken(response.data.accessToken);
      // Discard the GET cache of the previous (switched) context.
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(BaseApi.ROOT_URL))
        .forEach((key) => sessionStorage.removeItem(key));
      // Full re-bootstrap so ALL permissions/roles/modules reload cleanly from
      // /user-data (a partial in-place restore could leave the admin without
      // their full rights).
      reloadPage();
    }
  }).catch(() => {
    new ErrorDialog();
  });
}

export async function loadLayout() {
  const {isLoggedIn} = useAuth();
  if (isLoggedIn()) {
    await loadFooterLinks();
    await loadSocialMedia();
    await loadPartnerShops();
    await loadNotifications();
  }
}
